/**
 * Portable rollout-safety — see docs/FRONTEND_ASSET_DELIVERY_DESIGN.md (Layer 1c).
 *
 * After a deploy, a tab that was opened against the PREVIOUS build may lazy-load
 * a code-split chunk whose content hash no longer exists on the server. webpack
 * raises a `ChunkLoadError` (surfaced as an unhandled promise rejection), or the
 * injected <script>/<link> emits a resource `error` event. Left unhandled the
 * operator sees a broken/blank view. We recover by fetching a fresh, consistent
 * index.html via a single full reload — which, with hashed+immutable assets and a
 * no-cache index.html, always yields a matching bundle set.
 *
 * This is the CDN-independent safety net: it works the same on GCP/AWS/Azure and
 * in an air-gapped self-hosted VPC, because it needs no external origin. The
 * mixed-old/new-pod window (the other 404 source) is closed separately by the
 * Recreate deploy strategy; this handler covers the pre-deploy open-tab case.
 *
 * SAFETY — must never reload-loop. A genuinely missing asset (a bad deploy that a
 * reload cannot fix) is gated by a sessionStorage one-shot with a short cooldown:
 * we reload at most once per COOLDOWN_MS. If the asset is STILL failing after a
 * reload, we stop and let the existing error overlay surface the real problem.
 */

const RELOAD_AT_KEY = '__prove7_chunk_reload_at__';
const COOLDOWN_MS = 15000; // reload at most once per 15s per tab session

function recentlyReloaded() {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_AT_KEY) || 0);
    return last > 0 && Date.now() - last < COOLDOWN_MS;
  } catch {
    return false; // sessionStorage unavailable → fail safe: allow one reload
  }
}

function reloadOnce(reason) {
  if (recentlyReloaded()) {
    // Already fetched a fresh index.html and the asset STILL fails → this is a
    // real error, not a stale-chunk race. Do not loop; let it surface.
    // eslint-disable-next-line no-console
    console.error('[chunkReload] asset still failing after reload — not looping:', reason);
    return;
  }
  try {
    sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch {
    /* ignore — proceed with the reload anyway */
  }
  window.location.reload();
}

function isChunkLoadError(err) {
  if (!err) return false;
  const name = err.name || '';
  const msg = err.message || String(err);
  return (
    name === 'ChunkLoadError' ||
    /Loading( CSS)? chunk [\w-]+ failed/i.test(msg) ||
    /Loading chunk \d+ failed/i.test(msg)
  );
}

/**
 * Install the guard. Idempotent — safe to call once at startup.
 * Must run before the app renders so it is armed before any lazy import.
 */
export function installChunkReloadGuard() {
  if (typeof window === 'undefined') return;
  if (window.__prove7_chunk_guard_installed__) return;
  window.__prove7_chunk_guard_installed__ = true;

  // 1) webpack lazy import() failures surface as unhandled promise rejections.
  window.addEventListener('unhandledrejection', (e) => {
    if (isChunkLoadError(e && e.reason)) {
      reloadOnce((e.reason && (e.reason.message || e.reason.name)) || 'ChunkLoadError');
    }
  });

  // 2) A <script>/<link> that fails to load emits a resource `error` event.
  //    These do NOT bubble, so listen in the capture phase. Only act on our own
  //    hashed build assets to avoid reacting to unrelated third-party failures.
  window.addEventListener(
    'error',
    (e) => {
      const t = e && e.target;
      if (!t || t === window) return;
      const url = t.src || t.href || '';
      const tag = (t.tagName || '').toUpperCase();
      if ((tag === 'SCRIPT' || tag === 'LINK') && /\/static\/(js|css)\//.test(url)) {
        reloadOnce('resource ' + url);
      }
    },
    true
  );
}
